import React, { useState, useContext } from 'react';
import styled from '@emotion/styled';
import {
  breakpoints,
  OutboundLinkButton,
  Button,
  normalize,
  AuthorContext,
  routes
} from 'gatsby-theme-apollo';
import { FaDiscord, FaYoutube, FaTwitter, FaPatreon, FaTwitch, FaInstagram, FaLink } from 'react-icons/fa';
import { navigate, StaticQuery, graphql } from 'gatsby';
import { Twitter, YouTube, Instagram, Patreon, Twitch, Discord } from '../../util/social-routes';
import { Flex } from '../layout/flex';
import EditableText from '../text/editable-text';
import { updateAuthor } from '../../services/author-service';
import Section from '../layout/section';
import { validateString } from '../../util/validate';
import ErrorText from '../text/error-text';

const StyledUserLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin: 0px auto;
  max-width: 900px;
`;

const SocialLink = styled.div(props => ({
  backgroundColor: props.enableEditing && (props.color || `rgba(0, 0, 0, 0.5)`),
  borderRadius: props.theme.radius,
  display: `flex`,
  alignItems: `stretch`,
  justifyContent: `flex-start`,
  padding: `4px`,
  marginLeft: `1px`,
  width: props.enableEditing && `100%`
}));

const SocialLinkBox = styled.div(props => ({
  display: props.enableEditing ? `flex` : `inline-flex`,
  margin: `6px`,
  alignItems: `center`,
  flexDirection: props.enableEditing ? 'row' : 'column',
  justifyContent: 'center',
  textAlign: `center`,
  minWidth: props.enableEditing && `350px`,
  width: props.fullWidth ? `100%` : `auto`,
  flexGrow: props.enableEditing && 1,
  maxWidth: props.fullWidth ? `100%` : `45%`,
  [breakpoints.sm]: {
    maxWidth: `600px`,
    width: props.expand ? `100%` : `auto`
  }
}));

const Prefix = styled.span`
  color: rgba(255,255, 255, 0.8);
  backgroundColor: ${props => props.color || `rgba(0, 0, 0, 0.5)`},
  border-top-left-radius: ${props => props.theme.radius};
  border-bottom-left-radius: ${props => props.theme.radius};
  padding-left: 5px;
  padding-right: 3px;
`;

const Logo = styled.img({
  width: `40px`,
  height: `40px`,
});

const Network = ({
  prefix,
  atPrefix,
  href,
  icon,
  logoURI,
  networkName,
  enableEditing,
  text,
  color,
  onInputChanged,
  hideText,
  fullWidth
}) => {
  return (
    <>
      {(text || enableEditing) && (
        <SocialLinkBox expand={enableEditing} enableEditing={enableEditing} fullWidth={fullWidth}>
          {href && (
            <OutboundLinkButton
              style={{
                padding: `5px 2px`,
                backgroundColor: `transparent`,
                marginRight: `1px`
              }}
              dontExpand
              href={href}
              target={href && '_blank'}
              rel="nofollow noopener"
              aria-label={`${networkName} link`}
            >
              {icon}
              {logoURI && <Logo src={logoURI} />}
            </OutboundLinkButton>
          )}
          <SocialLink color={color} enableEditing={enableEditing}>
            <div
              style={{
                width: `100%`,
                display: `flex`,
                alignItems: `center`
              }}
            >
              {enableEditing && prefix && <Prefix>{prefix}</Prefix>}
              {enableEditing ? (
                <EditableText
                  key={networkName}
                  placeholder={networkName}
                  textAlign={enableEditing ? `left` : `center`}
                  inputType="text"
                  textValue={text}
                  editingDisabled={!enableEditing}
                  width={enableEditing && '100%'}
                  onInputChanged={e => {
                    if (onInputChanged) {
                      onInputChanged(e);
                    }
                  }}
                  dark
                />
              ) : (
                <span style={{ fontWeight: 'bold', fontSize: `18px` }}>
                  {!hideText && `${atPrefix ? `@` : ''}${text}`}
                </span>
              )}
            </div>
          </SocialLink>
        </SocialLinkBox>
      )}
    </>
  );
};

const SocialLinks = ({ profile }) => {
  const {
    name,
    twitterUsername,
    patreonUsername,
    youtubeUsername,
    twitchUsername,
    instagramUsername,
    websiteURI,
    discordURI,
    id: authorID,
    bio: authorBio
  } = profile;

  const [twitter, setTwitter] = useState(twitterUsername);
  const [youtube, setYoutube] = useState(youtubeUsername);
  const [twitch, setTwitch] = useState(twitchUsername);
  const [discord, setDiscord] = useState(discordURI);
  const [website, setWebsite] = useState(websiteURI);
  const [patreon, setPatreon] = useState(patreonUsername);
  const [instagram, setInstagram] = useState(instagramUsername);
  const [edited, setEdited] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [username, setUsername] = useState(name);
  const [bio, setBio] = useState(authorBio);
  const [changedUsername, setChangedUsername] = useState(false);

  const [usernameTaken, setUsernameTaken] = useState(false);

  const { author, ChangeName, isAuthorNameAvailable } = useContext(AuthorContext);
  const {
    valid: usernameValid,
    tooShort: usernameTooShort,
    illegalCharacters: usernameIllegalCharacters
  } = validateString(username, 3, /[^0-9a-zA-Z\s_-]/g);
  const signedInAuthorIsProfileOwner = author && author.id === authorID;

  const trySaveUserProfile = async () => {
    if (usernameValid) {
      const authorNameAvailable = await isAuthorNameAvailable({ name: username });
      if (!authorNameAvailable) {
        setUsernameTaken(true);
        return;
      }

      setSaving(true);
      const updatedAuthor = await updateAuthor({
        authorID,
        bio,
        twitterUsername: twitter,
        youtubeUsername: youtube,
        twitchUsername: twitch,
        discordURI: discord,
        patreonUsername: patreon,
        instagramUsername: instagram,
        websiteURI: website,
        name: username
      });
      if (updatedAuthor) {
        setSaving(false);
        setSaved(true);
        setEdited(false);
        if (changedUsername) {
          ChangeName(username);
          navigate(routes.User(username));
        }
      }
    }
  };

  return (
    <StaticQuery
      query={graphql`
        {
          site {
            siteMetadata {
              logoIconURI
            }
          }
        }
      `}
      render={data => {
        const { logoIconURI } = data.site.siteMetadata;
        return (
          <>
            <Section>
              {signedInAuthorIsProfileOwner && (
                <h4 style={{ width: `100%`, textAlign: `center`, padding: `40px` }}>
                  Help GamerHub users join your following!
                </h4>
              )}
              {signedInAuthorIsProfileOwner && usernameValid && (edited || saved) && (
                <Flex style={{ width: `100%`, padding: `15px` }}>
                  <Button onClick={() => trySaveUserProfile()} primary>
                    {!saving && (edited && !saved ? `Save Changes` : `Saved`)}
                    {saving && 'Saving'}
                  </Button>
                </Flex>
              )}

              <StyledUserLinks>
                {signedInAuthorIsProfileOwner && (
                  <Network
                    networkName="GamerHub Name"
                    logoURI={logoIconURI}
                    enableEditing
                    text={username}
                    prefix="gamerhub.gg/user/"
                    atPrefix
                    color="black"
                    onInputChanged={newValue => {
                      setUsername(newValue);
                      setChangedUsername(true);
                      setEdited(true);
                    }}
                  />
                )}
                <Network
                  icon={null}
                  href={''}
                  fullWidth
                  networkName="Bio"
                  text={bio}
                  enableEditing={signedInAuthorIsProfileOwner}
                  prefix=""
                  color="#222"
                  onInputChanged={newValue => {
                    setBio(newValue);
                    setEdited(true);
                  }}
                />
              </StyledUserLinks>

              <Flex style={{ width: '100%', flexDirection: `column`, justifyContent: `center` }}>
                <ErrorText
                  errors={[
                    {
                      show: usernameTooShort,
                      message: `Sorry, must be 3 characters!`
                    },
                    {
                      show: usernameIllegalCharacters,
                      message: 'Sorry, only letters and numbers!'
                    },
                    {
                      show: usernameTaken,
                      message: 'Sorry, that name is taken!'
                    }
                  ]}
                />
              </Flex>
              <StyledUserLinks>
                <Network
                  href={Twitter(twitter)}
                  icon={<FaTwitter />}
                  networkName="Twitter Username"
                  enableEditing={signedInAuthorIsProfileOwner}
                  text={twitter}
                  prefix="twitter.com/"
                  atPrefix
                  color="#00acee"
                  onInputChanged={newValue => {
                    setTwitter(newValue);
                    setEdited(true);
                  }}
                />
                <Network
                  href={YouTube(youtube)}
                  icon={<FaYoutube />}
                  networkName="YouTube Channel ID"
                  enableEditing={signedInAuthorIsProfileOwner}
                  prefix="youtube.com/channel/"
                  text={youtube}
                  color="#b2071d"
                  onInputChanged={newValue => {
                    setYoutube(newValue);
                    setEdited(true);
                  }}
                />
                <Network
                  href={Twitch(twitch)}
                  icon={<FaTwitch />}
                  networkName="Twitch Channel"
                  enableEditing={signedInAuthorIsProfileOwner}
                  atPrefix
                  prefix="twitch.tv/"
                  text={twitch}
                  color="#6441a5"
                  onInputChanged={newValue => {
                    setTwitch(newValue);
                    setEdited(true);
                  }}
                />
                <Network
                  href={Patreon(patreon)}
                  icon={<FaPatreon />}
                  networkName="Patreon Username"
                  prefix="patron.com/"
                  enableEditing={signedInAuthorIsProfileOwner}
                  atPrefix
                  text={patreon}
                  color="#E85B46"
                  onInputChanged={newValue => {
                    setPatreon(newValue);
                    setEdited(true);
                  }}
                />

                <Network
                  href={Instagram(instagram)}
                  icon={<FaInstagram />}
                  networkName="Instagram Username"
                  prefix="instagram.com/"
                  atPrefix
                  enableEditing={signedInAuthorIsProfileOwner}
                  text={instagram}
                  color="#3897F0"
                  onInputChanged={newValue => {
                    setInstagram(newValue);
                    setEdited(true);
                  }}
                />
                <Network
                  href={normalize.requireHttp(website)}
                  icon={<FaLink />}
                  networkName="Website Address"
                  prefix=""
                  enableEditing={signedInAuthorIsProfileOwner}
                  text={author ? website : normalize.stripHttp(website)}
                  color="#222"
                  onInputChanged={newValue => {
                    setWebsite(newValue);
                    setEdited(true);
                  }}
                />
                <Network
                  href={Discord(discord)}
                  icon={<FaDiscord />}
                  networkName="Discord Server"
                  prefix="discord.gg/"
                  enableEditing={signedInAuthorIsProfileOwner}
                  text={discord}
                  hideText
                  color="#7289DA"
                  onInputChanged={newValue => {
                    setDiscord(newValue);
                    setEdited(true);
                  }}
                />
              </StyledUserLinks>
            </Section>
          </>
        );
      }}
    />
  );
};

export default SocialLinks;
